import {Component, DestroyRef, ElementRef, OnInit, ViewChild, inject, signal} from '@angular/core';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';
import {CommonModule} from '@angular/common';
import {Router} from '@angular/router';
import {skip, Subscription} from 'rxjs';
import {
  PureOriginalTopUserStats,
  MasterExportStats,
  MasterExportTarget,
  MasterActivityPage,
  MasterActivityRow,
  MasterTopUserUploadStats,
  TrendGranularity
} from '../../models/dashboard-statistics.model';
import {DashboardStatisticsService} from '../../services/dashboard-statistics.service';
import {DashboardPeriodService, PeriodMode} from '../../../dashboard/services/dashboard-period.service';

interface AggregatedUser {
  userId: number;
  firstName: string;
  lastName: string;
  total: number;
}

interface AggregatedUploader {
  name: string;
  total: number;
}

@Component({
  selector: 'app-user-activity',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './user-activity.component.html',
  styleUrls: ['./user-activity.component.css']
})
export class UserActivityComponent implements OnInit {

  private readonly statsService = inject(DashboardStatisticsService);
  private readonly periodService = inject(DashboardPeriodService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  @ViewChild('masterActivityDialog') private masterActivityDialog!: ElementRef<HTMLDialogElement>;

  private readonly range$ = toObservable(this.periodService.currentRange);
  private poRequest?: Subscription;
  private masterStatsRequest?: Subscription;
  private masterActivityRequest?: Subscription;
  private loadedMasterRange: {start: string; end: string} | null = null;
  private masterActivityRange: {start: string; end: string} | null = null;
  readonly masterStatsLoading = signal(true);
  readonly masterActivityTarget = signal<MasterExportTarget | null>(null);
  readonly masterActivityRangeLabel = signal('');
  readonly masterActivityPage = signal<MasterActivityPage | null>(null);
  readonly masterActivityPageNumber = signal(0);
  readonly masterActivityLoading = signal(false);
  readonly masterActivityError = signal(false);

  poGranularity = signal<TrendGranularity>('DAY');
  // Master Upload Stats card has its own Day/Week/Month filter, independent of
  // the global date selector — it drives the card's own date range, not just bucketing.
  masterGranularity = signal<TrendGranularity>('DAY');
  readonly defaultVisibleUploaders = 5;
  showAllUploaders = signal(false);
  isLoading = signal<boolean>(true);
  isPoLoading = signal<boolean>(true);
  poLoadFailed = signal<boolean>(false);

  aggregatedPoUsers: AggregatedUser[] = [];
  aggregatedUploaders: AggregatedUploader[] = [];
  masterExportStats: MasterExportStats[] = [];

  readonly barColors: string[] = ['#7770CD', '#5AA155', '#E8A838', '#D33A3A', '#4A90D9'];
  readonly avatarGradients: string[] = [
    'linear-gradient(135deg, #7770CD, #5A54A4)',
    'linear-gradient(135deg, #5AA155, #3D7A39)',
    'linear-gradient(135deg, #E8A838, #C48A20)',
    'linear-gradient(135deg, #D33A3A, #A82B2B)',
    'linear-gradient(135deg, #4A90D9, #2E6BB5)'
  ];

  ngOnInit(): void {
    this.syncPoGranularityWithDashboard();
    this.loadPoUsers();
    this.loadMasterData();

    // Include custom date changes even when the dashboard remains in CUSTOM mode.
    this.range$.pipe(skip(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.syncPoGranularityWithDashboard();
      this.loadPoUsers();
    });
  }

  onPoGranularityChange(g: TrendGranularity): void {
    if (this.poGranularity() === g) return;
    this.poGranularity.set(g);
    this.loadPoUsers();
  }

  onMasterGranularityChange(g: TrendGranularity): void {
    this.closeMasterActivity();
    this.masterGranularity.set(g);
    this.showAllUploaders.set(false);
    this.isLoading.set(true);
    let pending = 2;
    const done = () => { if (--pending === 0) this.isLoading.set(false); };
    this.loadUploaders(done);
    this.loadMasterExportStats(done);
  }

  private loadMasterData(): void {
    this.isLoading.set(true);
    let pending = 2;
    const done = () => { if (--pending === 0) this.isLoading.set(false); };
    this.loadUploaders(done);
    this.loadMasterExportStats(done);
  }

  private syncPoGranularityWithDashboard(): void {
    const mode = this.periodService.mode();
    if (mode !== 'CUSTOM') {
      this.poGranularity.set(mode === 'TODAY' ? 'DAY' : mode);
    }
  }

  private poRange(): {start: string; end: string} {
    if (this.periodService.mode() === 'CUSTOM') {
      return {start: this.periodService.currentStart(), end: this.periodService.currentEnd()};
    }
    const granularity = this.poGranularity();
    return granularity === 'DAY'
      ? this.periodService.todayUntilNow()
      : this.periodService.presetRange(granularity);
  }

  /** Local date range for the Master Upload Stats card, derived from its own switch. */
  private masterRange(): {start: string; end: string} {
    const g = this.masterGranularity();
    const mode: Exclude<PeriodMode, 'CUSTOM'> = g === 'DAY' ? 'TODAY' : g;
    return this.periodService.presetRange(mode);
  }

  private loadPoUsers(): void {
    this.poRequest?.unsubscribe();
    this.isPoLoading.set(true);
    this.poLoadFailed.set(false);
    const {start, end} = this.poRange();
    this.poRequest = this.statsService.getTopPureOriginalUsers(start, end, this.poGranularity())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          this.aggregatedPoUsers = this.aggregateUsers(data);
          this.isPoLoading.set(false);
        },
        error: () => {
          this.aggregatedPoUsers = [];
          this.poLoadFailed.set(true);
          this.isPoLoading.set(false);
        }
      });
  }

  private loadUploaders(done?: () => void): void {
    const {start, end} = this.masterRange();
    this.statsService.getTopMasterUploads(start, end, this.masterGranularity())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          this.aggregatedUploaders = this.aggregateUploaders(data);
          done?.();
        },
        error: () => done?.()
      });
  }

  private loadMasterExportStats(done?: () => void): void {
    this.masterStatsRequest?.unsubscribe();
    this.masterStatsLoading.set(true);
    this.masterExportStats = [];
    this.loadedMasterRange = null;
    const {start, end} = this.masterRange();
    this.masterStatsRequest = this.statsService.getMasterExportStats(start, end)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          this.masterExportStats = data;
          this.loadedMasterRange = {start, end};
          this.masterStatsLoading.set(false);
          done?.();
        },
        error: () => {
          this.masterStatsLoading.set(false);
          done?.();
        }
      });
  }

  openMasterActivity(target: MasterExportTarget, count: number): void {
    if (count <= 0 || this.masterStatsLoading() || !this.loadedMasterRange) return;
    const {start, end} = this.loadedMasterRange;
    this.masterActivityRange = {start, end: this.toExclusiveEnd(end)};
    this.masterActivityTarget.set(target);
    this.masterActivityRangeLabel.set(`${start.replace('T', ' ')} – ${end.replace('T', ' ')}`);
    this.masterActivityPageNumber.set(0);
    if (!this.masterActivityDialog.nativeElement.open) this.masterActivityDialog.nativeElement.showModal();
    this.loadMasterActivityPage(0);
  }

  closeMasterActivity(): void {
    this.masterActivityDialog?.nativeElement.close();
    this.onMasterActivityClosed();
  }

  onMasterActivityClosed(): void {
    this.masterActivityRequest?.unsubscribe();
    this.masterActivityTarget.set(null);
    this.masterActivityPage.set(null);
    this.masterActivityRange = null;
    this.masterActivityLoading.set(false);
    this.masterActivityError.set(false);
  }

  goToMasterActivityPage(pageNumber: number): void {
    const page = this.masterActivityPage();
    if (pageNumber < 0 || (page && pageNumber >= page.totalPages) || this.masterActivityLoading()) return;
    this.loadMasterActivityPage(pageNumber);
  }

  retryMasterActivity(): void {
    this.loadMasterActivityPage(this.masterActivityPageNumber());
  }

  openMasterActivityDetails(row: MasterActivityRow): void {
    this.closeMasterActivity();
    this.router.navigate(['/app/master-details', row.masterId]);
  }

  formatMasterActivityDate(value: string | null): string {
    if (!value) return '—';
    const match = /^(\d{2})-(\d{2})-(\d{4})-(\d{2})-(\d{2})-(\d{2})$/.exec(value);
    return match ? `${match[1]}.${match[2]}.${match[3]} ${match[4]}:${match[5]}:${match[6]}` : value.replace('T', ' ');
  }

  private loadMasterActivityPage(pageNumber: number): void {
    const target = this.masterActivityTarget();
    const range = this.masterActivityRange;
    if (!target || !range) return;
    this.masterActivityRequest?.unsubscribe();
    this.masterActivityPageNumber.set(pageNumber);
    this.masterActivityPage.set(null);
    this.masterActivityLoading.set(true);
    this.masterActivityError.set(false);
    this.masterActivityRequest = this.statsService.getMasterActivity(range.start, range.end, target, pageNumber, 20)
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: page => {
          this.masterActivityPage.set(page);
          this.masterActivityLoading.set(false);
        },
        error: () => {
          this.masterActivityError.set(true);
          this.masterActivityLoading.set(false);
        }
      });
  }

  private toExclusiveEnd(end: string): string {
    const date = new Date(end);
    date.setSeconds(date.getSeconds() + 1);
    const pad = (value: number) => String(value).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
      `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  }

  private aggregateUsers(raw: PureOriginalTopUserStats[]): AggregatedUser[] {
    const map = new Map<number, AggregatedUser>();
    for (const entry of raw) {
      const existing = map.get(entry.userId);
      if (existing) {
        existing.total += entry.totalUpdates;
      } else {
        map.set(entry.userId, {
          userId: entry.userId,
          firstName: entry.firstName,
          lastName: entry.lastName,
          total: entry.totalUpdates
        });
      }
    }
    return Array.from(map.values())
      .sort((a, b) => b.total - a.total)
      .slice(0, 5);
  }

  private aggregateUploaders(raw: MasterTopUserUploadStats[]): AggregatedUploader[] {
    const latestPeriod = this.getLatestPeriod(raw.map(r => r.period));
    const filtered = latestPeriod
      ? raw.filter(r => r.period === latestPeriod)
      : raw;

    const map = new Map<string, number>();
    for (const entry of filtered) {
      map.set(entry.uploadedBy, (map.get(entry.uploadedBy) || 0) + entry.count);
    }
    return Array.from(map.entries())
      .map(([name, total]) => ({name, total}))
      .sort((a, b) => b.total - a.total);
  }

  private getLatestPeriod(periods: string[]): string | null {
    if (!periods.length) return null;
    return [...new Set(periods)].sort().pop() || null;
  }

  get visibleUploaders(): AggregatedUploader[] {
    return this.showAllUploaders()
      ? this.aggregatedUploaders
      : this.aggregatedUploaders.slice(0, this.defaultVisibleUploaders);
  }

  toggleUploaders(): void {
    this.showAllUploaders.update(expanded => !expanded);
  }

  trackUploader(_index: number, uploader: AggregatedUploader): string {
    return uploader.name;
  }

  get maxPoUserTotal(): number {
    if (this.aggregatedPoUsers.length === 0) return 1;
    return Math.max(...this.aggregatedPoUsers.map(u => u.total), 1);
  }

  get maxUploaderTotal(): number {
    if (this.aggregatedUploaders.length === 0) return 1;
    return Math.max(...this.aggregatedUploaders.map(u => u.total), 1);
  }

  get totalUploaded(): number {
    return this.masterExportStats.reduce((sum, s) => sum + s.uploaded, 0);
  }

  get totalUpdated(): number {
    return this.masterExportStats.reduce((sum, s) => sum + s.updated, 0);
  }

  getPoBarWidth(total: number): string {
    return ((total / this.maxPoUserTotal) * 100) + '%';
  }

  getUploaderBarWidth(total: number): string {
    return ((total / this.maxUploaderTotal) * 100) + '%';
  }

  getInitials(firstName: string, lastName: string): string {
    const first = firstName ? firstName.charAt(0).toUpperCase() : '';
    const last = lastName ? lastName.charAt(0).toUpperCase() : '';
    return first + last || '?';
  }

  getUploaderInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  }

  getAvatarGradient(index: number): string {
    return this.avatarGradients[index % this.avatarGradients.length];
  }

  getBarColor(index: number): string {
    return this.barColors[index % this.barColors.length];
  }
}
